import { Button, Drawer, Field, Input, Portal } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaFilter } from 'react-icons/fa6';

const FilterActions = ({ filter, changeFilter }: { filter: string; changeFilter: (value: string) => void }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { t } = useTranslation();
  const [filters, setFilters] = useState({
    name: '',
    priority: ['all'],
    archived: ['all'],
    locked: ['all'],
    start_date: '',
    end_date: '',
    impact: ['all'],
  });

  useEffect(() => {
    if (filter) {
      const parsedFilters: { [key: string]: any } = {};

      // Split the filter parameter (e.g., name=task&priority=high)
      const filterParams = new URLSearchParams(filter);

      // Populate the filters state with the values from the query parameter
      filterParams.forEach((value, key) => {
        if (key in filters) {
          if (key === 'priority' || key === 'archived' || key === 'locked' || key === 'impact') {
            parsedFilters[key] = value.split(','); // Handle multi-value filters
          } else {
            parsedFilters[key] = value;
          }
        }
      });

      // Update the filters state
      setFilters((prev) => ({
        ...prev,
        ...parsedFilters,
      }));
    }
  }, [filter]);

  const handleFilterChange = (column: string, value: any) => {
    setFilters((prev) => ({ ...prev, [column]: value }));
  };

  const applyFilters = () => {
    const cleanedFilters = Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== '' && JSON.stringify(value) !== JSON.stringify(['all']))
    );
    const encodedFilter = new URLSearchParams(
      Object.entries(cleanedFilters).map(([key, value]) => {
        if (Array.isArray(value)) {
          return [key, value.join(',')];
        }
        return [key, value];
      })
    ).toString();

    changeFilter(encodedFilter.replace(/\+/g, '%20'));
    setIsDrawerOpen(false);
  };

  useEffect(() => {
    // Vérifie si le tiroir est ouvert et qu'on écoute les événements de clavier
    if (isDrawerOpen) {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          applyFilters(); // Appelle la fonction pour appliquer les filtres
        }
      };

      // Ajoute l'écouteur d'événements
      window.addEventListener('keydown', handleKeyDown);

      // Nettoyage de l'écouteur lorsqu'on ferme le tiroir
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isDrawerOpen, filters]);

  return (
    <Drawer.Root
      open={isDrawerOpen}
      onOpenChange={(e) => setIsDrawerOpen(e.open)}
      size="sm"
      placement="end"
      onEscapeKeyDown={() => applyFilters()}
    >
      <Drawer.Trigger asChild>
        <Button variant="outline" size="sm" float="right">
          <FaFilter /> {t('ui.filter')}
        </Button>
      </Drawer.Trigger>
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content
            rounded="md"
            position="absolute"
            margin="{spacing.4}"
            h="calc(100% - {spacing.4} * 2)"
            right={0}
            top={0}
          >
            <Drawer.CloseTrigger />
            <Drawer.Header>{t('action.drawer.filter.title')}</Drawer.Header>
            <Drawer.Body>
              <Field.Root>
                <Field.Label>{t('action.filter.name.contains')}</Field.Label>
                <Input
                  placeholder={t('action.filter.name.contains.placeholder')}
                  value={filters.name}
                  onChange={(e) => handleFilterChange('name', e.target.value)}
                />
              </Field.Root>

              <Field.Root mt={4}>
                <Field.Label>{t('action.filter.startDate.select')}</Field.Label>
                <Input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => handleFilterChange('start_date', e.target.value)}
                />
              </Field.Root>

              <Field.Root mt={4}>
                <Field.Label>{t('action.filter.endDate.select')}</Field.Label>
                <Input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => handleFilterChange('end_date', e.target.value)}
                />
              </Field.Root>
            </Drawer.Body>
            <Drawer.Footer>
              <Button variant="outline" mr={3} onClick={() => setIsDrawerOpen(false)}>
                {t('ui.cancel')}
              </Button>
              <Button colorScheme="blue" onClick={applyFilters}>
                {t('ui.apply.filter')}
              </Button>
            </Drawer.Footer>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  );
};

export default FilterActions;
